// client/src/v2/nova/entrada-curta.tsx — A ENTRADA CURTA da conta nova (31/10/2026).
//
// Quem chega pelo Instagram cria a conta e já cai na interface nova. Antes
// passava pela abertura, pelo questionário do protocolo ("Onde você está
// agora?"), pelo Explorar e, na 1ª ida à Hoje, pelo tutorial antigo ("Vamos
// deixar tudo pronto") — quatro telas até a primeira nota. Agora são duas: a
// abertura "Bem-vindo, <nome>" e esta tela, que leva direto à trilha do
// Munger (a 1ª etapa é grátis; no app pago de 01/11, "7 dias grátis, sem
// cartão"). "Explorar o app primeiro" leva ao Explorar.
//
// 02/11: a pessoa ESCOLHE o livro. Quem veio de um Reels de Hábitos Atômicos
// caía no Munger; agora a tela mostra as trilhas abertas (temporada +
// biografias, lidas de /api/curadoria) e começa a do livro tocado. Sem a
// lista (rede caída), fica o Munger de antes.
//
// Uma vez só: quem decide é o App (tutorials_state.entrada no perfil). As
// duas telas nunca ficam uma sobre a outra — a da trilha só monta quando a
// abertura termina.

import React, { useEffect, useState } from 'react';
import { Abertura } from '../habito/preparo';
import { NvCapa } from './home-nova';
import { toast } from '../toast';
import { modeloPago, diasDoTeste, soAssinantes } from './modelo';
import { habilidadeEscolhida } from './pagina-assinar';
import { api, type CuradoriaLivro } from '../../services/apiV2';
import type { AuthSession } from '../../types';
import './nova.css';

export const MUNGER_SLUG = 'a-sabedoria-de-charlie-munger-kaufman';

/** O livro que já vem marcado: o carro-chefe, se estiver aberto. */
const PREFERIDO = 'habitos-atomicos-clear';
const RESERVA: Opcao = { slug: MUNGER_SLUG, titulo: 'A sabedoria de Charlie Munger', capa: `/acervo/capas/${MUNGER_SLUG}.jpg` };
type Opcao = { slug: string; titulo: string; capa: string | null };

export const EntradaCurta: React.FC<{
  nome?: string;
  session: AuthSession | null;
  /** Põe o livro na estante (se faltar) e abre a Travessia dele. */
  onComecar: (slug: string) => Promise<void>;
  onExplorar: () => void;
}> = ({ nome, session, onComecar, onExplorar }) => {
  const [fase, setFase] = useState<'abertura' | 'trilha'>('abertura');
  const [ocupado, setOcupado] = useState(false);
  const [opcoes, setOpcoes] = useState<Opcao[] | null>(null);
  const [escolhido, setEscolhido] = useState<string>(MUNGER_SLUG);

  // Carrega durante a abertura: quando ela termina, a lista já está aqui.
  useEffect(() => {
    let vivo = true;
    api.curadoria(session).then((c) => {
      if (!vivo) return;
      const todos: CuradoriaLivro[] = [...(c.temporada?.livros || []), ...(c.biografias?.livros || [])];
      const abertos = todos.filter((l) => l.travessia === 'disponivel').map((l) => ({ slug: l.slug, titulo: l.titulo, capa: l.capa }));
      if (!abertos.length) return;
      setOpcoes(abertos);
      // A habilidade que a pessoa veio ver (a DM do Reel) ou tocou na página de assinar.
      const veio = habilidadeEscolhida();
      setEscolhido((abertos.find((l) => l.slug === veio) || abertos.find((l) => l.slug === PREFERIDO) || abertos[0]).slug);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [session]);

  const lista = opcoes || [RESERVA];
  const livro = lista.find((l) => l.slug === escolhido) || lista[0];

  const comecar = async () => {
    if (ocupado) return;
    setOcupado(true);
    try { await onComecar(livro.slug); }
    catch { toast.error('Não deu para abrir a trilha agora. Tente de novo.'); setOcupado(false); }
  };

  return (
    <div className="nv-entrada" data-entrada-curta data-fase={fase} role="dialog" aria-modal="true">
      {fase === 'abertura' ? (
        <Abertura nome={nome} onFim={() => setFase('trilha')} />
      ) : (
        <div className="nv-entrada-corpo nv-entra">
          <span className="nv-mono nv-entrada-rotulo">A sua primeira trilha</span>
          <h1 className="nv-serif">Qual livro você vai ler?</h1>
          <p className="nv-entrada-sub">Escolha o livro que você tem ou vai comprar. As outras trilhas ficam no Explorar.</p>
          <div className="nv-entrada-livros" role="radiogroup" aria-label="Escolha o livro" data-entrada-livros>
            {lista.map((l) => (
              <button key={l.slug} type="button" role="radio" aria-checked={l.slug === livro.slug}
                className={`nv-entrada-livro${l.slug === livro.slug ? ' nv-escolhido' : ''}`}
                onClick={() => setEscolhido(l.slug)} disabled={ocupado} data-livro={l.slug}>
                <NvCapa src={l.capa || `/acervo/capas/${l.slug}.jpg`} titulo={l.titulo} w={92} h={138} />
                <span>{l.titulo}</span>
              </button>
            ))}
          </div>
          {modeloPago() ? (
            <>
              <p>O Mentor libera a nota certa na página certa do seu livro.</p>
              {/* Só assinantes: quem chega aqui já assinou — sem linha de oferta. */}
              {!soAssinantes() && <p className="nv-entrada-oferta" data-entrada-oferta>{diasDoTeste()} dias grátis, sem cartão. Depois, R$ 29,90 por ano.</p>}
            </>
          ) : (
            <p>A 1ª etapa é grátis: o Mentor libera a nota certa na página certa do seu livro.</p>
          )}
          <button className="nv-primario" onClick={comecar} disabled={ocupado} data-comecar-trilha>
            {ocupado ? 'Abrindo a trilha…' : 'Começar a trilha'}
          </button>
          <button className="nv-link nv-entrada-explorar" onClick={onExplorar} disabled={ocupado} data-explorar-primeiro>
            Explorar o app primeiro
          </button>
        </div>
      )}
    </div>
  );
};

export default EntradaCurta;
